import { useState, useMemo } from 'react';
import { css } from '@emotion/react';
import {
  IRadioButtonGroupProps,
  PanelCard,
  Table,
  Row,
  RangePicker,
  Space,
  Text,
  colors,
  Button,
  Col,
  Avatar,
} from 'common-components';
import { useAppTranslation } from '@hooks';

import FiltersContributor from './FiltersContributor';
import ContributorTag, { TContributorsTag } from './TagContributor';

const radioButtonGroupProps = {
  size: 'md',
  value: 1,
  items: [
    { value: 1, label: 'Option 1', iconLeft: 'Speedometer02' },
    { value: 2, label: 'Option 2', iconLeft: 'Flash' },
  ],
};

type MetricType = {
  key: string
  name: string
  metric?: string
  value: string
  progressType: TContributorsTag
  progress: string
};

const dummyData: MetricType[] = [
  {
    key: '1',
    name: 'Saisie Centre Paris',
    metric: 'Vitesse',
    value: '1,2',
    progressType: 'negative',
    progress: '20%',
  },
  {
    key: '2',
    name: 'Saisie Centre Paris',
    metric: 'Vitesse',
    value: '9,8',
    progressType: 'positive',
    progress: '30%',
  },
  {
    key: '3',
    name: 'Saisie Centre Paris',
    metric: 'Qualité',
    value: '4,4',
    progressType: 'negative',
    progress: '10%',
  },
  {
    key: '4',
    name: 'Saisie Centre Paris',
    metric: 'Qualité',
    value: '1,5',
    progressType: 'positive',
    progress: '40%',
  },
];

const dummyData2: MetricType[] = [
  {
    key: '1',
    name: 'Maroc ',
    value: '$1,800',
    progressType: 'negative',
    progress: '20%',
  },
  {
    key: '2',
    name: 'Maroc ',
    value: '$1,800',
    progressType: 'negative',
    progress: '20%',
  },
  {
    key: '3',
    name: 'Maroc ',
    value: '$1,800',
    progressType: 'negative',
    progress: '20%',
  },
  {
    key: '4',
    name: 'Maroc ',
    value: '$1,800',
    progressType: 'negative',
    progress: '20%',
  },
  {
    key: '5',
    name: 'Maroc ',
    value: '$1,800',
    progressType: 'negative',
    progress: '20%',
  },
  {
    key: '6',
    name: 'Maroc ',
    value: '$1,800',
    progressType: 'negative',
    progress: '20%',
  },
  {
    key: '7',
    name: 'Maroc ',
    value: '$1,800',
    progressType: 'negative',
    progress: '20%',
  },
  {
    key: '8',
    name: 'Maroc ',
    value: '$1,800',
    progressType: 'negative',
    progress: '20%',
  },
  {
    key: '9',
    name: 'Maroc ',
    value: '$1,800',
    progressType: 'negative',
    progress: '20%',
  },
  {
    key: '10',
    name: 'Maroc ',
    value: '$1,800',
    progressType: 'negative',
    progress: '20%',
  },
];

const MetricsContributor = () => {
  const [value1, setValue1] = useState<number | string>(1);
  const [value2, setValue2] = useState<number | string>(1);
  const [value3, setValue3] = useState<number | string>(1);
  const [isFilterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const { translate } = useAppTranslation();
  const tableColumns = useMemo(() => {
    try {
      return [
        {
          title: translate('contributor.project'),
          key: 'name',
          sorter: (a: MetricType, b: MetricType) => a.name.localeCompare(b.name),
          render: (record: MetricType) => (
            <Row align={'middle'}>
              <Space size={'middle'}>
                <Text size="sm">{record.name}</Text>
              </Space>
            </Row>
          ),
        },
        {
          title: translate('contributor.objective'),
          key: 'metric',
          sorter: (a: MetricType, b: MetricType) => a.name.localeCompare(b.name),
          render: (record: MetricType) => (
            <Row
              align={'middle'}
              css={css`
                min-width: 150px;
              `}
            >
              <Space size={'middle'}>
                <Text size="sm">{record.metric}</Text>
              </Space>
            </Row>
          ),
        },
        {
          title: '',
          key: 'value',
          render: (record: MetricType) => (
            <Row align={'middle'}>
              <Space size={'middle'}>
                <Text size="sm">{record.value}</Text>
                <ContributorTag state={record.progressType}>{record.progress}</ContributorTag>
              </Space>
            </Row>
          ),
        },
      ];
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return [];
    }
  }, [translate]);

  const tableColumns2 = useMemo(
    () => [
      {
        title: translate('contributor.project'),
        key: 'name',
        sorter: (a: MetricType, b: MetricType) => a.name.localeCompare(b.name),
        render: (record: MetricType) => (
          <Row
            align={'middle'}
            css={css`
              min-width: 900px;
            `}
          >
            <Space size={'middle'} style={{ gap: 12 }}>
              <Avatar size="sm" />
              <Text size="sm" strong>
                {record.name}
              </Text>
            </Space>
          </Row>
        ),
      },
      {
        title: translate('contributor.quality'),
        key: 'value',
        render: (record: MetricType) => (
          <Row align={'middle'}>
            <Space size={'middle'}>
              <Text size="sm">{record.value}</Text>
              <ContributorTag state={record.progressType}>{record.progress}</ContributorTag>
            </Space>
          </Row>
        ),
      },
    ],
    [translate],
  );

  const content = (
    <div
      style={{
        display: 'flex',
        height: '200px',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      Graph Content
    </div>
  );

  return (
    <div
      css={css`
        margin-bottom: 20px;
      `}
    >
      <Row justify={'space-between'}>
        <RangePicker />
        <Row
          css={css`
            gap: 12px;
          `}
        >
          <Button iconLeft="Download01">{translate('contributor.export')}</Button>
          <Button
            iconLeft="FilterLines"
            onClick={() => {
              setFilterDrawerVisible(true);
            }}
          >
            {translate('contributor.filter')}
          </Button>
        </Row>
      </Row>
      <div
        css={css`
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-gap: 24px;
          width: 100%;
          margin-top: 20px;
        `}
      >
        <PanelCard
          title={translate('contributor.volume')}
          // TOFIX: maybe this prop should accept ReactNode so we could pass any tag?
          tagText={translate('contributor.top20Percent')}
          width={596}
          content={content}
          value={value1}
          onChange={(e: any) => setValue1(e.target.value)}
          radioButtonGroupProps={radioButtonGroupProps as IRadioButtonGroupProps}
        />
        <PanelCard
          title={translate('contributor.quality')}
          value={value2}
          onChange={(e: any) => setValue2(e.target.value)}
          width={596}
          content={content}
          radioButtonGroupProps={radioButtonGroupProps as IRadioButtonGroupProps}
        />
        <PanelCard
          title={translate('contributor.averageCompletionTime')}
          value={value3}
          onChange={(e: any) => setValue3(e.target.value)}
          width={596}
          content={content}
          radioButtonGroupProps={radioButtonGroupProps as IRadioButtonGroupProps}
        />
        <PanelCard
          title={translate('contributor.table')}
          onChange={(e: any) => setValue1(e.target.value)}
          width={596}
          content={
            <div
              css={css`
                margin-top: 10px;
                width: 100%;
              `}
            >
              <Table<MetricType> data={dummyData} columns={tableColumns} />
            </div>
          }
        />
      </div>
      <Row
        justify={'space-between'}
        align={'middle'}
        css={css`
          border: 1px solid ${colors.gray200};
          border-radius: 12px;
          padding: 24px 26px;
          margin-top: 20px;
        `}
      >
        <Col
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
          `}
        >
          <Text
            css={css`
              font-size: 18px;
              line-height: 28px;
              font-weight: 700;
            `}
          >
            {translate('contributor.skills')}
          </Text>
        </Col>
      </Row>
      <Table<MetricType> data={dummyData2} columns={tableColumns2} />
      <FiltersContributor
        visible={isFilterDrawerVisible}
        onClose={() => setFilterDrawerVisible(false)}
      />
    </div>
  );
};

export default MetricsContributor;
